import $ from "cash-dom";
import { Loader } from "@googlemaps/js-api-loader";
import { mapStyle } from "@scripts/components/index/map-style.js";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import FlexSearch from "flexsearch";
import { DateTime } from "luxon";

const GEOLOCATION_TIMEOUT_MS = 7000;
const REST_PAGE_SIZE = 100;
const MAX_BRANCHES_TO_LOAD = 200;
const DEFAULT_CARD_LIMIT = 21;
const MAP_IDLE_DEBOUNCE_MS = 250;

const debounce = (delay, callback) => {
  let timeout = null;

  return (...args) => {
    if (timeout) clearTimeout(timeout);

    timeout = setTimeout(() => {
      timeout = null;
      callback(...args);
    }, delay);
  };
};

const getBranchPosition = (branch) => {
  const lat = Number(branch?.acf?.latitude);
  const lng = Number(branch?.acf?.longitude);

  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const distanceFrom = (branch, point) => {
  const branchPosition = getBranchPosition(branch);

  if (!branchPosition || !point) return Infinity;

  return (
    Math.pow(branchPosition.lat - point.lat, 2) +
    Math.pow(branchPosition.lng - point.lng, 2)
  );
};

const sortByDistance = (branches, point) =>
  [...branches].sort((a, b) => {
    const distanceDifference = distanceFrom(a, point) - distanceFrom(b, point);

    return distanceDifference || Number(a.id) - Number(b.id);
  });

const markerSignature = (branches) =>
  branches
    .map((branch) => branch.id)
    .sort((a, b) => Number(a) - Number(b))
    .join(",");

const plainText = (html = "") => {
  const container = document.createElement("div");
  container.innerHTML = String(html);

  return container.textContent?.trim() ?? "";
};

const escapeHtmlAttribute = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      })[character],
  );

const loadBranches = async () => {
  await wp.api.loadPromise;
  const collection = new wp.api.collections.Pobocky();
  const branches = await collection.fetch({
    data: {
      per_page: REST_PAGE_SIZE,
    },
  });

  while (collection.hasMore() && branches.length < MAX_BRANCHES_TO_LOAD) {
    branches.push(...(await collection.more()));
  }

  return branches.filter(getBranchPosition);
};

export const indexMapComponent = async () => {
  const roots = $(".map").get();

  if (!roots.length) return;

  const branchPromise = loadBranches();
  const loader = new Loader({
    apiKey: "AIzaSyDYwzW-0vp9EFCvj8VkSQ087_-hJUakIcA",
    version: "weekly",
    region: "SK",
    language: "sk",
  });

  const { Map, InfoWindow } = await loader.importLibrary("maps");
  const { AdvancedMarkerElement, PinElement } =
    await loader.importLibrary("marker");

  return Promise.all(
    roots.map(async (root) => {
      const $root = $(root);
      const $mapContainer = $root.find(".map-container");
      const $search = $root.find(".map-search-input");
      const $cards = $root.find("#branches-map-cards");
      const $cardTemplate = $root.find("#map-card-template");
      const $showMore = $root.find(".map-show-more");
      const $showMoreLabel = $showMore.find(".map-show-more-label");
      const $showMoreIcon = $showMore.find(".map-show-more-icon");
      const configuredInitialCardCount = Number(
        $cards.attr("data-initial-cards") ?? 2,
      );
      const initialCardCount = Number.isFinite(configuredInitialCardCount)
        ? Math.max(1, configuredInitialCardCount)
        : 2;
      const configuredCardLimit = Number(
        $cards.attr("data-max-cards") ?? DEFAULT_CARD_LIMIT,
      );
      const cardLimit = Math.min(
        Number.isFinite(configuredCardLimit)
          ? configuredCardLimit
          : DEFAULT_CARD_LIMIT,
        DEFAULT_CARD_LIMIT,
      );

      const cards = {};
      const markers = {};
      const index = new FlexSearch.Index({
        charset: "latin:simple",
        tokenize: "forward",
      });

      const state = {
        clusterMarkerSignature: "",
        geolocationTimer: null,
        initialFraming: false,
        initializationPromise: null,
        cardsExpanded: false,
        searchActive: false,
        skipNextIdleUpdate: false,
      };

      let branches = [];
      let clusterer;
      let map;

      const getMapCenter = () => {
        const center = map?.getCenter();

        return center ? { lat: center.lat(), lng: center.lng() } : null;
      };

      const createCard = (branch) => {
        const $card = $cardTemplate.clone();
        const titleId = `map-card-title-${branch.id}`;
        const linkTextId = `map-card-link-text-${branch.id}`;

        $card.attr("id", `map-card-${branch.id}`);
        $card.removeClass("hidden");
        $card
          .find(".map-card-title")
          .attr("id", titleId)
          .html(branch.title.rendered);
        $card
          .find(".map-card-address")
          .html(
            `${branch.acf.address}<br>${branch.acf.zip_code} ${branch.acf.city}`,
          );
        $card.find(".map-card-link-text").attr("id", linkTextId);
        $card.find(".map-card-link").attr({
          href: branch.link,
          "aria-labelledby": `${linkTextId} ${titleId}`,
        });

        $card.find(".phone-link").each((number, element) => {
          const $element = $(element);
          const phone = branch.acf[number ? "phone_2" : "phone"];

          if (!phone) {
            $element.addClass("hidden");
            return;
          }

          $element.attr("href", `tel:${phone}`);
          $element.text(phone);
        });

        const openingHourKeys = [
          "",
          "opening_hours_monday",
          "opening_hours_tuesday",
          "opening_hours_wednesday",
          "opening_hours_thursday",
          "opening_hours_friday",
          "opening_hours_saturday",
          "opening_hours_sunday",
        ];
        const openingHours =
          branch.acf[openingHourKeys[DateTime.now().weekday]] || "";

        $card.find(".map-card-opening-hours").text(openingHours);

        if (openingHours.includes("-")) {
          const [start, end] = openingHours.split("-");
          const isAfterStart =
            DateTime.fromFormat(start.trim(), "H:m").diffNow().as("seconds") <
            0;
          const isBeforeEnd =
            DateTime.fromFormat(end.trim(), "H:m").diffNow().as("seconds") > 0;

          $card
            .find(".map-card-opening-hours-light")
            .css("color", isAfterStart && isBeforeEnd ? "#419F5A" : "#E61414");
        }

        return $card;
      };

      const createMarkers = () => {
        branches.forEach((branch) => {
          const position = getBranchPosition(branch);

          if (!position) return;

          const pin = new PinElement({
            background: "#E61414",
            borderColor: "#AF1414",
            glyphColor: "#821414",
          });
          const marker = new AdvancedMarkerElement({
            position,
            content: pin.element,
            map: null,
          });
          const infowindow = new InfoWindow({
            content: `<strong>${branch.acf.address}</strong><br>${branch.acf.city}<br><a class="underline hover:no-underline text-red-dark font-medium" target="_blank" href="${branch.link}" aria-label="${escapeHtmlAttribute(`Detail pobočky: ${plainText(branch.title?.rendered)}`)}">Detail pobočky</a>`,
          });

          marker.addListener("click", () => {
            infowindow.open(map, marker);
          });

          markers[branch.id] = marker;
          cards[branch.id] = createCard(branch);
          index.add(
            branch.id,
            [
              branch.title?.rendered ?? "",
              branch.acf?.city ?? "",
              branch.acf?.address ?? "",
              branch.acf?.zip_code ?? "",
            ].join(" "),
          );
        });

        clusterer = new MarkerClusterer({
          map,
          markers: Object.values(markers),
        });
        state.clusterMarkerSignature = markerSignature(branches);
      };

      const setVisibleMarkers = (visibleBranches) => {
        const nextSignature = markerSignature(visibleBranches);

        if (nextSignature === state.clusterMarkerSignature) return;

        clusterer.clearMarkers();
        clusterer.addMarkers(
          visibleBranches.map((branch) => markers[branch.id]).filter(Boolean),
        );
        state.clusterMarkerSignature = nextSignature;
      };

      const updateShowMore = (cardCount) => {
        const hasMoreCards = cardCount > initialCardCount;

        if (!hasMoreCards) {
          state.cardsExpanded = false;
          $showMore.addClass("hidden");
          return;
        }

        $showMore.removeClass("hidden");
        $showMore.attr("aria-expanded", String(state.cardsExpanded));
        $showMoreLabel.text(
          state.cardsExpanded ? "Zobraziť menej" : "Zobraziť viac",
        );

        if (state.cardsExpanded) {
          $showMoreIcon.addClass("rotate-180");
        } else {
          $showMoreIcon.removeClass("rotate-180");
        }
      };

      const setCardsExpanded = (expanded) => {
        state.cardsExpanded = expanded;
        const $extraCards = $cards.find("[data-map-card-extra]");

        $extraCards.each((_, element) => {
          const $element = $(element);

          if (expanded) {
            $element.removeClass("hidden");
          } else {
            $element.addClass("hidden");
          }
        });

        updateShowMore($cards.children().length);
      };

      const renderCards = (visibleBranches, emptyMessage) => {
        const branchesForCards = visibleBranches.slice(0, cardLimit);

        $cards.empty();
        state.cardsExpanded = false;

        if (!branchesForCards.length) {
          updateShowMore(0);
          $cards.append(`
            <div class="w-full py-6 text-gray-dark">
              ${emptyMessage}
            </div>
          `);
          return;
        }

        branchesForCards.forEach((branch, index) => {
          const $card = cards[branch.id];

          if (!$card) return;

          $card.removeClass("hidden");

          if (index >= initialCardCount) {
            $card.attr("data-map-card-extra", "");
            $card.addClass("hidden");
          } else {
            $card.removeAttr("data-map-card-extra");
          }

          $cards.append($card);
        });

        updateShowMore(branchesForCards.length);
      };

      const getBranchesInBounds = () => {
        const bounds = map?.getBounds();

        if (!bounds) return [];

        return branches.filter((branch) => {
          const position = getBranchPosition(branch);

          return position && bounds.contains(position);
        });
      };

      const renderBranchesInCurrentBounds = () => {
        const visibleBranches = sortByDistance(
          getBranchesInBounds(),
          getMapCenter(),
        );

        renderCards(
          visibleBranches,
          "Nenašli sa žiadne pobočky v zobrazenej oblasti.",
        );
      };

      const getBoundsForBranches = (branchesToFrame) => {
        const bounds = new google.maps.LatLngBounds();
        let hasBounds = false;

        branchesToFrame.forEach((branch) => {
          const position = getBranchPosition(branch);

          if (!position) return;

          bounds.extend(position);
          hasBounds = true;
        });

        return hasBounds ? bounds : null;
      };

      const frameInitialMap = (mode, userPosition) => {
        const nearbyBranches = sortByDistance(branches, userPosition).slice(
          0,
          cardLimit,
        );
        const branchesToFrame = mode === "nearby" ? nearbyBranches : branches;
        const bounds = getBoundsForBranches(branchesToFrame);

        if (mode === "nearby") {
          renderCards(
            nearbyBranches,
            "Nenašli sa žiadne pobočky v okolí vašej polohy.",
          );
        }

        if (!bounds) {
          state.initialFraming = false;
          renderBranchesInCurrentBounds();
          return;
        }

        state.initialFraming = true;
        google.maps.event.addListenerOnce(map, "idle", () => {
          state.initialFraming = false;
          state.skipNextIdleUpdate = true;

          if (mode === "fallback" && !state.searchActive) {
            renderBranchesInCurrentBounds();
          }
        });
        if (mode === "fallback") {
          map.setCenter({
            lat: 48.67,
            lng: 19.7,
          });

          map.setZoom(7.6);
        } else {
          map.fitBounds(bounds, 32);
        }
      };

      const handleSearch = (input) => {
        const value =
          typeof input === "string"
            ? input.trim()
            : String(input?.target?.value ?? "").trim();

        if (value.length < 3) {
          state.searchActive = false;
          setVisibleMarkers(branches);
          renderBranchesInCurrentBounds();
          return;
        }

        state.searchActive = true;
        const matchedIds = new Set(index.search(value).map(String));
        const matchingBranches = sortByDistance(
          branches.filter((branch) => matchedIds.has(String(branch.id))),
          getMapCenter(),
        );

        setVisibleMarkers(matchingBranches);
        renderCards(
          matchingBranches,
          "Nenašli sa žiadne pobočky pre zadané vyhľadávanie.",
        );

        const searchBounds = getBoundsForBranches(matchingBranches);

        if (searchBounds) map.fitBounds(searchBounds, 32);
      };

      const initialize = (mode, userPosition = null) => {
        if (state.initializationPromise) return state.initializationPromise;

        state.initializationPromise = (async () => {
          branches = await branchPromise;
          const fallbackBounds = getBoundsForBranches(branches);

          const initialCenter =
            mode === "nearby" && userPosition
              ? userPosition
              : (fallbackBounds?.getCenter() ?? { lat: 48.7, lng: 19.7 });

          map = new Map($mapContainer.get(0), {
            center: initialCenter,
            zoom: mode === "nearby" ? 9 : 7,
            disableDefaultUI: true,
            styles: mapStyle,
            mapId: "7930465941fdbb2d",
          });

          createMarkers();
          map.addListener(
            "idle",
            debounce(MAP_IDLE_DEBOUNCE_MS, () => {
              if (state.initialFraming || state.searchActive) return;

              if (state.skipNextIdleUpdate) {
                state.skipNextIdleUpdate = false;
                return;
              }

              renderBranchesInCurrentBounds();
            }),
          );
          $search.on("input", debounce(300, handleSearch));
          $showMore.on("click", () => {
            setCardsExpanded(!state.cardsExpanded);
          });

          frameInitialMap(mode, userPosition);

          const initialSearch = String($search.val() ?? "").trim();

          if (initialSearch.length >= 3) handleSearch(initialSearch);
        })();

        return state.initializationPromise;
      };

      const clearGeolocationTimer = () => {
        if (!state.geolocationTimer) return;

        clearTimeout(state.geolocationTimer);
        state.geolocationTimer = null;
      };

      const initializeFallback = () => {
        clearGeolocationTimer();
        return initialize("fallback");
      };

      if (!("geolocation" in navigator)) {
        initializeFallback();
        return;
      }

      state.geolocationTimer = setTimeout(() => {
        initializeFallback();
      }, GEOLOCATION_TIMEOUT_MS);

      navigator.geolocation.getCurrentPosition(
        (geoposition) => {
          clearGeolocationTimer();
          initialize("nearby", {
            lat: geoposition.coords.latitude,
            lng: geoposition.coords.longitude,
          });
        },
        () => {
          initializeFallback();
        },
        {
          timeout: GEOLOCATION_TIMEOUT_MS,
        },
      );
    }),
  );
};
